import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DragDropContext,
  Droppable,
  Draggable
} from '@hello-pangea/dnd';
import { toast } from 'react-hot-toast';
import investorsService, {
  Investor,
  InvestorOpportunityStage,
  ListInvestorsParams
} from '../../../services/investorCrm/investorsService';
import { useInvestorCrmOrgStore } from '../../../store/investorCrmOrg.store';
import { INVESTOR_STAGE_OPTIONS } from '../../../components/investor-crm/constants';
import InvestorDrawer from '../../../components/investor-crm/InvestorDrawer';

interface Column {
  stage: InvestorOpportunityStage;
  title: string;
  description: string;
}

const BOARD_COLUMNS: Column[] = [
  { stage: 'prospecting', title: 'Prospecting', description: 'Identify investors to research or contact.' },
  { stage: 'warm_intro', title: 'Warm intro', description: 'Introductions secured via network.' },
  { stage: 'first_meeting', title: 'First meeting', description: 'Initial call/meeting scheduled or completed.' },
  { stage: 'diligence', title: 'Diligence', description: 'Investor reviewing data room or exploring fit.' },
  { stage: 'term_sheet', title: 'Term sheet', description: 'Negotiating or reviewing a term sheet.' },
  { stage: 'committed', title: 'Committed', description: 'Soft commitments secured, paperwork pending.' },
  { stage: 'closed_won', title: 'Closed won', description: 'Investment closed with funds received.' },
  { stage: 'closed_lost', title: 'Closed lost', description: 'Investor declined or paused for now.' }
];

type BoardState = Record<InvestorOpportunityStage, Investor[]>;

const PipelineTab: React.FC = () => {
  const { activeOrgId } = useInvestorCrmOrgStore();
  const [loading, setLoading] = useState(false);
  const [board, setBoard] = useState<BoardState>(() => ({
    prospecting: [],
    warm_intro: [],
    first_meeting: [],
    diligence: [],
    term_sheet: [],
    committed: [],
    closed_won: [],
    closed_lost: []
  }));
  const [activeInvestorId, setActiveInvestorId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchBoard = useCallback(async () => {
    if (!activeOrgId) {
      setBoard({
        prospecting: [],
        warm_intro: [],
        first_meeting: [],
        diligence: [],
        term_sheet: [],
        committed: [],
        closed_won: [],
        closed_lost: []
      });
      return;
    }

    setLoading(true);

    try {
      const params: ListInvestorsParams = {
        organization_id: activeOrgId,
        limit: 200,
        offset: 0
      };

      const { data } = await investorsService.list(params);
      const nextBoard: BoardState = {
        prospecting: [],
        warm_intro: [],
        first_meeting: [],
        diligence: [],
        term_sheet: [],
        committed: [],
        closed_won: [],
        closed_lost: []
      };

      data?.forEach((investor) => {
        const stage = investor.opportunity_stage ?? 'prospecting';
        nextBoard[stage].push(investor);
      });

      setBoard(nextBoard);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load investor pipeline');
    } finally {
      setLoading(false);
    }
  }, [activeOrgId]);

  const updateInvestorStage = useCallback(async (
    investor: Investor,
    nextStage: InvestorOpportunityStage
  ) => {
    const orgId = investor.organization_id ?? activeOrgId;
    if (!orgId) {
      toast.error('No organization context found for this investor');
      throw new Error('Missing organization context');
    }

    await investorsService.updateStage(investor.id, orgId, nextStage);
  }, [activeOrgId]);

  const handleDragEnd = useCallback(async (result: any) => {
    if (!result.destination) return;

    const sourceStage = result.source.droppableId as InvestorOpportunityStage;
    const destinationStage = result.destination.droppableId as InvestorOpportunityStage;

    if (sourceStage === destinationStage && result.source.index === result.destination.index) return;

    const previousBoard = board;
    const sourceColumn = Array.from(previousBoard[sourceStage]);
    const [movedInvestor] = sourceColumn.splice(result.source.index, 1);
    const destinationColumn = Array.from(previousBoard[destinationStage]);
    destinationColumn.splice(result.destination.index, 0, movedInvestor);

    const optimistic: BoardState = {
      ...previousBoard,
      [sourceStage]: sourceColumn,
      [destinationStage]: destinationColumn
    };
    setBoard(optimistic);

    try {
      await updateInvestorStage(movedInvestor, destinationStage);
      toast.success('Stage updated');
    } catch {
      setBoard(previousBoard);
    }
  }, [board, updateInvestorStage]);

  useEffect(() => {
    void fetchBoard();
  }, [fetchBoard]);

  const stageLabelMap = useMemo(() => {
    const map = new Map<InvestorOpportunityStage, string>();
    INVESTOR_STAGE_OPTIONS.forEach((option) => {
      map.set(option.value, option.label);
    });
    return map;
  }, []);

  return (
    <div className="space-y-6">
      {loading ? (
        <div className="rounded-xl bg-white p-10 text-center text-sm text-gray-500 shadow-sm ring-1 ring-gray-100">
          Loading investor pipeline…
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {BOARD_COLUMNS.map((column) => {
              const investors = board[column.stage] ?? [];
              return (
                <div
                  key={column.stage}
                  className="flex flex-col rounded-xl bg-white shadow-sm ring-1 ring-gray-100"
                >
                  <div className="border-b border-gray-100 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">{column.title}</h3>
                        <p className="mt-1 text-xs text-gray-500">{column.description}</p>
                      </div>
                      <span className="rounded-full bg-primary/10 px-2 py-1 text-xs font-semibold text-primary">
                        {investors.length}
                      </span>
                    </div>
                  </div>
                  <Droppable droppableId={column.stage}>
                    {(provided: any, snapshot: any) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`flex-1 space-y-3 p-4 transition ${snapshot.isDraggingOver ? 'bg-primary/5' : 'bg-white'}`}
                      >
                        {investors.map((investor, index) => (
                          <Draggable key={investor.id} draggableId={investor.id} index={index}>
                            {(dragProvided: any, dragSnapshot: any) => (
                              <div
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                                className={`cursor-pointer rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition hover:shadow-md ${dragSnapshot.isDragging ? 'shadow-lg ring-2 ring-primary/30' : ''}`}
                                onClick={() => {
                                  setActiveInvestorId(investor.id);
                                  setDrawerOpen(true);
                                }}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="font-semibold text-gray-900">{investor.company_name}</div>
                                </div>
                                {investor.fund_focus && (
                                  <div className="mt-2 text-xs text-gray-500">{investor.fund_focus}</div>
                                )}
                                {investor.notes && (
                                  <div className="mt-2 line-clamp-2 text-xs text-gray-400">{investor.notes}</div>
                                )}
                                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                                  {investor.contact_name && <span>👤 {investor.contact_name}</span>}
                                  {investor.check_size_min != null && (
                                    <span>
                                      💰 £{Intl.NumberFormat().format(investor.check_size_min)}
                                      {investor.check_size_max != null ? `-£${Intl.NumberFormat().format(investor.check_size_max)}` : '+'}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        {investors.length === 0 && (
                          <div className="rounded-lg border border-dashed border-gray-200 p-6 text-center text-xs text-gray-400">
                            Drop investors here
                          </div>
                        )}
                      </div>
                    )}
                  </Droppable>
                </div>
              );
            })}
          </div>
        </DragDropContext>
      )}
      <InvestorDrawer
        organizationId={activeOrgId ?? null}
        investorId={activeInvestorId}
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onUpdated={() => void fetchBoard()}
      />
    </div>
  );
};

export default PipelineTab;
